/**
 * SoleadLab 2.0 - Create Project Modal
 *
 * Lightweight modal for creating a new server project from within the editor.
 * Used when user tries to save a local-only project (e.g. imported from ZIP).
 */

import { useState, useEffect } from 'react';
import Modal from './Modal';
import Button from './Button';
import { createProject, listClients } from '../../services/projectApi';
import type { ClientItem } from '../../services/projectApi';
import './LoginModal.css'; // Reuse login modal field styles

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (siteId: string, siteName: string) => void;
  defaultName?: string;
}

export default function CreateProjectModal({ isOpen, onClose, onCreated, defaultName = '' }: CreateProjectModalProps) {
  const [name, setName] = useState(defaultName);
  const [clientId, setClientId] = useState('');
  const [clients, setClients] = useState<ClientItem[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill name when modal opens or defaultName changes
  useEffect(() => {
    if (isOpen) {
      setName(defaultName);
      setError(null);
    }
  }, [isOpen, defaultName]);

  // Fetch clients when modal opens
  useEffect(() => {
    if (!isOpen) return;
    listClients()
      .then((c) => {
        setClients(c);
        if (c.length > 0 && !clientId) setClientId(c[0].id);
      })
      .catch(() => {
        // Non-admin users may not have access — proceed without clients
      });
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setCreating(true);
    setError(null);
    try {
      const site = await createProject(name.trim(), clientId || clients[0]?.id || undefined);
      onCreated(site.id, site.name);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create Server Project" size="sm">
      <div className="login-modal">
        <form onSubmit={handleSubmit} className="login-modal__form">
          <p className="login-modal__description" style={{ marginBottom: 0 }}>
            Create a server project to enable saving and auto-sync.
          </p>

          <div className="login-modal__field">
            <label className="login-modal__label">Project Name</label>
            <input
              className="login-modal__input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter project name"
              autoFocus
            />
          </div>

          {clients.length > 1 && (
            <div className="login-modal__field">
              <label className="login-modal__label">Client</label>
              <select
                className="login-modal__input"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
              >
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          {error && (
            <div className="login-modal__error">{error}</div>
          )}

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
            <Button variant="ghost" size="sm" type="button" onClick={onClose}>Cancel</Button>
            <Button variant="primary" size="sm" type="submit" disabled={!name.trim() || creating} loading={creating}>
              Create Project
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
